import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Route } from '../models/route';
import { BootstrapGuard } from '../../bootstrap/bootstrap.guard';

@Injectable()
export class RouteService {

    protected routes = [];

    protected flatRoutes = [];

    protected flatRoutePaths = {};

    protected layouts: Array<any> = [];

    /**
     * Route service constructor.
     *
     * @param router
     * @param breadcrumb
     */
    constructor(private router: Router/*, public breadcrumb: BreadcrumbService*/) {
        this.iterate(this.router.config);
    }

    /**
     * Return router instance
     *
     * @returns {Router}
     */
    public getRouter() {
        return this.router;
    }

    /**
     * Register a layout component
     *
     * @param name
     * @param component
     * @param defaultLayout
     * @param defaultGuard
     * @returns {RouteService}
     */
    public registerLayout(name: string, component: Object, defaultLayout: boolean = false, defaultGuard = null) {
        this.layouts.push({
            name: name,
            component: component,
            default: defaultLayout ? 1 : 0,
            defaultGuard: defaultGuard
        });

        return this;
    }

    /**
     * Return all routes.
     *
     * @returns {Array}
     */
    public all() {
        return this.routes;
    }

    /**
     * Return the route with the given name.
     *
     * @param name
     * @returns Route
     */
    public get(name: string): Route {
        return this.flatRoutes.filter((route) => {
            return route.name === name;
        }).reverse()[0];
    }

    /**
     * Returns the path of the route with the given name.
     *
     * @param name
     * @param parameters
     * @returns {string}
     */
    public route(name: string, parameters?: string | number | Array<string | number>): string {

        const route = this.get(name);

        if (route === undefined) {
            throw new Error('Given route name (' + name + ') has no defined routes');
        }

        if (route.path === undefined) {
            throw new Error('Undefined path for route ' + route.name);
        }

        let fullRoutePath = this.flatRoutePaths[name];

        if (parameters) {
            const routeParameters = this.getPathParameters(fullRoutePath);

            if ((parameters instanceof Array ? parameters.length : 1) !== routeParameters.length) {
                throw new Error('Given parameters do not match with route parameters on route: ' + route.name);
            }

            if (parameters instanceof Array) {
                for (const key in routeParameters) {
                    if (routeParameters.hasOwnProperty(key)) {
                        fullRoutePath = fullRoutePath.replace(':' + routeParameters[key], parameters[key]);
                    }
                }
            } else {
                fullRoutePath = fullRoutePath.replace(':' + routeParameters[0], parameters);
            }
        }

        return '/' + fullRoutePath
    }

    protected getPathParameters(path: string = '') {
        const parameters = [];

        if (path.includes(':')) {

            const chunks = path.split('/');

            for (const param in chunks) {
                if (chunks.hasOwnProperty(param)) {
                    if (chunks[param].includes(':')) {
                        const key = chunks[param].replace(':', '');
                        parameters.push(key);
                    }
                }
            }
        }

        return parameters;
    }

    /**
     * Returns the route with the given path.
     *
     * @param path
     * @returns Route
     */
    public routeWithPath(path: string): Route {

        return this.routes.filter((route) => {
            return route.path === path;
        }).reverse()[0];

    }

    /**
     * Add a new route.
     *
     * @param path
     * @param component
     * @param name
     * @param data
     */
    public add(path: string | Array<string>, component: Object, name: string, data?: Object): this {
        if (this.getDefaultLayout() === undefined) {
            throw new Error('No default layout was configured, set one first');
        }

        const routePath = (path instanceof Array) ? path : [path];

        for (const item of routePath) {
            // See if we need to set a default layout
            if (typeof data === 'undefined') {
                data = {};
            }

            if (data['layout'] === undefined) {
                data['layout'] = this.getDefaultLayout().name;
            }

            if (!data['canActivate'] && this.getLayout(data['layout']).defaultGuard !== null) {
                data['canActivate'] = [this.getLayout(data['layout']).defaultGuard];
                data['canActivate'].push(BootstrapGuard);
            } else {
                data['canActivate'] = [BootstrapGuard];
            }

            const route = new Route(item, component, name, data);

            // Check if a route with the given path already exists.
            if (this.routeExists(route)) {
                // Replace the existing route.
                this.routes.forEach((item: Route, index: number) => {
                    if (item.name === name) {
                        this.routes[index] = route;

                        this.addRouteChildrenToFlatRoutes(route);
                    }
                });
            } else {
                this.addRouteToRouteList(route);
            }
        }

        return this;
    }

    protected routeExists(route) {
        return !!this.flatRoutes.filter((item: Route) => {
            return item.name === name;
        }).length;
    }

    protected addRouteToRouteList(route) {
        this.routes.push(route);
        this.flatRoutes.push(route);
        this.flatRoutePaths[route.name] = route.path;

        this.addRouteChildrenToFlatRoutes(route);
    }

    protected addRouteChildrenToFlatRoutes(route) {
        if (route.children) {
            route.children.forEach((child) => {
                child.name = route.name + '.' + child.name;

                this.flatRoutes.push(child);
                this.flatRoutePaths[child.name] = route.path + (!!child.path ? '/' + child.path : '');
            });
        }
    }

    /**
     * Add an alias to a route.
     *
     * @param route
     * @param name
     */
    public alias(route: string, name: string) {
        const r = this.get(route);

        this.add(r.path, r.component, name, r.data);

        return this;
    }

    /**
     * Add a secondary path for a route.
     *
     * @param route
     * @param path
     */
    public forward(route: string, path: string) {
        const r = this.get(route);

        this.add(path, r.component, r.name, r.data);

        return this;
    }

    /**
     * Remove an existing route.
     *
     * @param name
     */
    public delete(name: string): RouteService {
        this.routes = this.routes.filter((item) => {
            return item.name !== name;
        });

        return this;
    }

    /**
     * Navigate to path by given route name
     *
     * @param parameters
     * @param name
     */
    public navigate(name: string, parameters?: string | number | Array<string | number>): Promise<boolean> {
        return this.router.navigateByUrl(this.route(name, parameters));
    }

    public navigateRaw(commands, extras: Object = {}) {
        return this.router.navigate(commands, extras);
    }

    /**
     * Apply changes to Angular router.
     */
    public apply() {
        const routes = this.layouts
            .sort((a, b) => b['default'] - a['default'])
            .map((layout) => {
                return {
                    path: '',
                    component: layout.component,
                    outlet: layout.outlet ? layout.outlet : null,
                    children: this.getWhere('layout', layout.name)
                };
            });

        this.router.resetConfig(routes);
    }

    /**
     * Iterate trough a list of routes and add them to a flat array.
     *
     * @param routes
     */
    private iterate(routes: Array<any>): void {
        for (const route of routes) {
            if (route.children) {
                // Reformat children routes to support named routes
                for (const child of route.children) {
                    child.data['name'] = child.name;
                    child.path         = route.path + '/' + child.path;
                }

                this.iterate(route.children);
            } else {
                this.routes[route.data['name']] = route.path;
            }
        }
    }

    /**
     * Get routes by query
     *
     * @param dataKey
     * @param value
     * @returns {any[]}
     */
    private getWhere(dataKey: string, value: any) {

        return this.routes.filter((route) => route.data[dataKey] === value);
    }

    /**
     * Get the default layout
     *
     * @returns {any}
     */
    private getDefaultLayout() {
        return this.layouts.find(layout => layout.default);
    }

    /**
     *
     * @returns {undefined|any}
     */
    private getLayout(name: string) {
        return this.layouts.find(layout => layout.name === name);
    }

    public get params(): any {
        let params = {};
        let route  = this.router.routerState.snapshot.root;

        do {
            params = Object.assign(params, route.params);
            route  = route.firstChild;
        } while (route);

        return params;
    }

    public get currentUrl(): string {
        const route = this.router.routerState.snapshot.root;

        return route['_routerState'].url;
    }

    public get segments(): Array<string> {
        const segments = this.currentUrl.split('/');

        return segments.filter(segment => segment !== '');
    }
}

export class RouteMockService {

    public route(name) {
        return '/';
    }

}


// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/routing/services/route.service.ts